import { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Search,
  Download,
  Eye,
  Edit,
  Send,
  Package,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Building,
  Truck,
  User,
  Grid3X3,
  List
} from 'lucide-react';
import '../styles/OrdenesCompra.css';

const OrdenesCompra = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todas');
  const [currency, setCurrency] = useState('CLP');
  const [viewMode, setViewMode] = useState('table');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const exchangeRates = {
    CLP: 1,
    USD: 950,
    UF: 36500
  };

  const convertCurrency = (amountCLP, toCurrency) => {
    const amount = parseFloat(amountCLP);
    if (toCurrency === 'CLP') {
      return `$${amount.toLocaleString('es-CL')}`;
    } else if (toCurrency === 'USD') {
      const usdAmount = (amount / exchangeRates.USD);
      return `US$${usdAmount.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (toCurrency === 'UF') {
      const ufAmount = (amount / exchangeRates.UF);
      return `${ufAmount.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} UF`;
    }
    return `$${amount.toLocaleString('es-CL')}`;
  };

  const ordenesCompra = [
    {
      id: 'OC-2025-001',
      ordenVenta: 'OV-2025-001',
      proveedor: 'WEIR Chile',
      contacto: 'Roberto Díaz',
      fecha: '2025-01-14',
      entrega: '2025-02-10',
      items: 5,
      monto: 98760000,
      estado: 'Enviada',
      descripcion: 'Sistema de filtrado para planta',
      confirmada: false,
      tracking: ''
    },
    {
      id: 'OC-2025-002',
      ordenVenta: 'OV-2025-002',
      proveedor: 'Komatsu Chile',
      contacto: 'Andrea López',
      fecha: '2025-01-14',
      entrega: '2025-02-05',
      items: 15,
      monto: 36543120,
      estado: 'Confirmada',
      descripcion: 'Válvulas alta presión WEIR 3000PSI',
      confirmada: true,
      tracking: 'KOM-2025-789'
    },
    {
      id: 'OC-2025-003',
      ordenVenta: 'OV-2025-003',
      proveedor: '3M Chile',
      contacto: 'Felipe Muñoz',
      fecha: '2025-01-13',
      entrega: '2025-01-28',
      items: 8,
      monto: 18760000,
      estado: 'Borrador',
      descripcion: 'Repuestos Komatsu 930E-4',
      confirmada: false,
      tracking: ''
    },
    {
      id: 'OC-2025-004',
      ordenVenta: 'OV-2025-004',
      proveedor: 'MSA Chile',
      contacto: 'Claudia Pérez',
      fecha: '2025-01-12',
      entrega: '2025-01-25',
      items: 23,
      monto: 63120000,
      estado: 'En Tránsito',
      descripcion: 'EPP certificado para altura',
      confirmada: true,
      tracking: 'MSA-2025-456'
    },
    {
      id: 'OC-2025-005',
      ordenVenta: 'OV-2025-005',
      proveedor: 'Fenner Dunlop',
      contacto: 'Miguel Castro',
      fecha: '2025-01-10',
      entrega: '2025-02-15',
      items: 12,
      monto: 45424000,
      estado: 'Confirmada',
      descripcion: 'Correas transportadoras 72"',
      confirmada: true,
      tracking: 'FD-2025-123'
    },
    {
      id: 'OC-2025-006',
      ordenVenta: '',
      proveedor: 'SKF Chile',
      contacto: 'Patricia Vargas',
      fecha: '2025-01-09',
      entrega: '2025-01-20',
      items: 30,
      monto: 12340000,
      estado: 'Completada',
      descripcion: 'Rodamientos industriales varios',
      confirmada: true,
      tracking: 'SKF-2025-987'
    }
  ];

  const filteredOrdenes = ordenesCompra.filter(orden => {
    const matchesSearch =
      orden.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      orden.proveedor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      orden.descripcion.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'todas' ||
      orden.estado.toLowerCase().replace(' ', '-') === filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'completada': return 'status-completada';
      case 'confirmada': return 'status-confirmada';
      case 'enviada': return 'status-enviada';
      case 'borrador': return 'status-borrador';
      case 'en tránsito': return 'status-transito';
      default: return '';
    }
  };

  const getStatusStyle = (status) => {
    if (status.toLowerCase() === 'en tránsito') {
      return {
        color: themeColor,
        borderColor: themeColor,
        border: `2px solid ${themeColor}`,
        padding: '4px 8px',
        borderRadius: '4px',
        fontWeight: '600'
      };
    }
    return {};
  };

  const getStatusIcon = (status) => {
    return null; // Sin íconos en los estados
  };

  return (
    <div className="ordenes-compra-container">
      <div className="ordenes-compra-header">
        <div className="header-title">
          <FileText size={28} />
          <h1>Órdenes de Compra</h1>
        </div>
        <button
          className="btn-nueva-orden"
          style={{
            backgroundColor: themeColor,
            color: '#000',
            border: '2px solid #000'
          }}
        >
          <Plus size={20} />
          Nueva Orden de Compra
        </button>
      </div>

      <div className="ordenes-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FileText size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>OC Activas</p>
            <h3>18</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Clock size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>Por Confirmar</p>
            <h3>5</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Truck size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>En Tránsito</p>
            <h3>7</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DollarSign size={24} color="#000" />
          </div>
          <div className="stat-content">
            <p>Valor Total Mes</p>
            <h3>{convertCurrency(274947120, currency)}</h3>
          </div>
        </div>
      </div>

      <div className="ordenes-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por ID, proveedor o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="todas">Todas</option>
            <option value="borrador">Borrador</option>
            <option value="enviada">Enviadas</option>
            <option value="confirmada">Confirmadas</option>
            <option value="en-transito">En Tránsito</option>
            <option value="completada">Completadas</option>
          </select>
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              style={viewMode === 'table' ? {
                backgroundColor: themeColor,
                color: '#000',
                borderColor: '#000'
              } : {}}
            >
              <List size={16} />
              Tabla
            </button>
            <button
              className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              style={viewMode === 'card' ? {
                backgroundColor: themeColor,
                color: '#000',
                borderColor: '#000'
              } : {}}
            >
              <Grid3X3 size={16} />
              Cards
            </button>
          </div>
          <select
            className="currency-select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="CLP">CLP</option>
            <option value="USD">USD</option>
            <option value="UF">UF</option>
          </select>
          <button className="btn-export">
            <Download size={20} />
            Exportar
          </button>
        </div>
      </div>

{viewMode === 'table' ? (
        <div className="ordenes-table-container">
          <table className="ordenes-table">
            <thead>
              <tr>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>ID OC</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>OV Ref.</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Proveedor</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Contacto</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Descripción</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Items</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Valor</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Fecha</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Entrega</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Estado</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Tracking</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrdenes.map((orden) => (
                <tr key={orden.id}>
                  <td className="id-cell">{orden.id}</td>
                  <td className="ref-cell">
                    {orden.ordenVenta || <span className="no-ref">Sin OV</span>}
                  </td>
                  <td>
                    <div className="provider-cell">
                      <Building size={16} />
                      {orden.proveedor}
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <User size={14} />
                      {orden.contacto}
                    </div>
                  </td>
                  <td className="description-cell" title={orden.descripcion}>
                    {orden.descripcion.length > 30
                      ? `${orden.descripcion.substring(0, 30)}...`
                      : orden.descripcion}
                  </td>
                  <td className="items-cell">{orden.items}</td>
                  <td className="amount-cell">{convertCurrency(orden.monto, currency)}</td>
                  <td>{orden.fecha}</td>
                  <td>{orden.entrega}</td>
                  <td>
                    <span
                      className={`status ${getStatusColor(orden.estado)}`}
                      style={getStatusStyle(orden.estado)}
                    >
                      {getStatusIcon(orden.estado)}
                      {orden.estado}
                    </span>
                  </td>
                  <td className="tracking-cell">
                    {orden.tracking ? (
                      <span className="tracking-id">{orden.tracking}</span>
                    ) : (
                      <span className="no-tracking">-</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-action" title="Ver">
                        <Eye size={16} />
                      </button>
                      <button className="btn-action" title="Editar">
                        <Edit size={16} />
                      </button>
                      <button className="btn-action btn-send" title="Enviar">
                        <Send size={16} />
                      </button>
                      {orden.tracking && (
                        <button className="btn-action btn-track" title="Tracking">
                          <Truck size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ordenes-cards">
          {filteredOrdenes.map((orden) => (
            <div key={orden.id} className="orden-card">
              <div className="card-header">
                <div className="card-id">
                  <h3>{orden.id}</h3>
                  <span
                    className={`status ${getStatusColor(orden.estado)}`}
                    style={getStatusStyle(orden.estado)}
                  >
                    {getStatusIcon(orden.estado)}
                    {orden.estado}
                  </span>
                </div>
                <div className="card-amount">
                  <span className="amount-value">{convertCurrency(orden.monto, currency)}</span>
                </div>
              </div>
              <div className="card-content">
                <div className="client-info">
                  <div className="client-name">
                    <Building size={16} />
                    <span>{orden.proveedor}</span>
                  </div>
                  <div className="contact-info">
                    <User size={14} />
                    <span>{orden.contacto}</span>
                  </div>
                  <div className="ref-info">
                    {orden.ordenVenta ? (
                      <span>OV Ref: {orden.ordenVenta}</span>
                    ) : (
                      <span className="no-ref">Sin OV asociada</span>
                    )}
                  </div>
                </div>
                <div className="description">
                  <p>{orden.descripcion}</p>
                </div>
                <div className="card-details">
                  <div className="detail-row">
                    <div className="detail-item">
                      <strong>Items:</strong> {orden.items}
                    </div>
                    <div className="detail-item">
                      <Calendar size={14} />
                      <span>Fecha: {orden.fecha}</span>
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <Truck size={14} />
                      <span>Entrega: {orden.entrega}</span>
                    </div>
                    <div className="detail-item">
                      {orden.tracking ? (
                        <>
                          <Package size={14} />
                          <span>Tracking: {orden.tracking}</span>
                        </>
                      ) : (
                        <span className="no-tracking">Sin tracking</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-actions">
                <button className="btn-action" title="Ver">
                  <Eye size={16} />
                </button>
                <button className="btn-action" title="Editar">
                  <Edit size={16} />
                </button>
                <button className="btn-action btn-send" title="Enviar">
                  <Send size={16} />
                </button>
                {orden.tracking && (
                  <button className="btn-action btn-track" title="Tracking">
                    <Truck size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdenesCompra;